import React from 'react';
import Tool, { type ToolHandler } from './Tool';
import type { AssistantToolCall } from 'components/framework/models';
import type Agent from '../agents/Agent';
import { contentToEditorContent, contentToHtml } from './utils/contentToHtml';
import { jsonFetch } from 'javascripts/fetch';
import { showNoticeMessage, showAlertMessage } from 'javascripts/messages';
import { Page } from 'components/framework/models';
import addToSvg from '../assets/icons/addTo.svg';
import inspectElementSvg from '../assets/icons/inspectElement.svg';

function insertDiscoveryInsightRow(
  agent: Agent,
  existingInsightRowId: string | undefined,
  existingInsightRowName: string | undefined,
  name: string | undefined,
  content: string | undefined,
  contentType: string | undefined
): void {
  if (!content) return;
  const editorContent = contentToEditorContent(
    contentToHtml(content, contentType)
  );
  const editorJSON = JSON.stringify({ children: editorContent });

  // Covnert name from a filename to a title remove any file extension and replace - or _ with a space
  if (name) {
    name = name
      .replace(/\.[^/.]+$/, '') // Remove file extension
      .replace(/[-_]/g, ' '); // Replace - or _ with a space
  }

  let params: {
    discovery_insight_row: {
      name?: string;
      insight_type?: 'ai_artifact';
      description_attributes: {
        editor_content?: string;
        append_editor_content?: string;
      };
    };
    discovery_interview_id?: string;
    discovery_study_id?: string;
  };
  let url: string;
  let method: 'POST' | 'PATCH';

  if (existingInsightRowId) {
    url = `/discovery_insight_rows/${existingInsightRowId}`;
    method = 'PATCH';
    params = {
      discovery_insight_row: {
        description_attributes: {
          append_editor_content: editorJSON,
        },
      },
    };
  } else {
    url = `/discovery_insight_rows`;
    method = 'POST';
    params = {
      discovery_insight_row: {
        name,
        insight_type: 'ai_artifact',
        description_attributes: {
          editor_content: editorJSON,
        },
      },
    };
  }

  if (agent.agentParams.insightable === 'Interview') {
    params.discovery_interview_id = agent.agentParams.interviewId;
  } else if (agent.agentParams.insightable === 'Study') {
    params.discovery_study_id = agent.agentParams.studyId;
  }

  jsonFetch(url, {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(params),
  })
    .then(response => {
      if (response.ok) {
        if (existingInsightRowId) {
          showNoticeMessage(
            `Added artifact to ${existingInsightRowName} on ${agent.agentParams.insightable.toLowerCase()} insight report.`
          );
        } else {
          showNoticeMessage(
            `Added artifact to the ${agent.agentParams.insightable.toLowerCase()} insight report.`
          );
        }
      } else {
        console.error('Error adding insight row:', response.statusText);
      }
    })
    .catch(error => {
      console.error('Error adding insight row:', error);
    });
}

function getDiscoveryAdds(
  toolCall: AssistantToolCall,
  agent: Agent
): React.ReactNode {
  if (!agent.agentParams.insightable) {
    return null;
  }
  const items: React.ReactNode[] = [];

  if (agent.agentParams.existingInsightRows?.length > 0) {
    agent.agentParams.existingInsightRows.map(row => {
      items.push(
        <aha-menu-item
          key={row.id}
          onClick={() =>
            insertDiscoveryInsightRow(
              agent,
              row.id,
              row.name,
              toolCall.content?.preview?.title ||
                toolCall.content?.panel?.title,
              toolCall.content?.panel?.body,
              toolCall.content?.panel?.contentType
            )
          }
        >
          Add to {row.name}
        </aha-menu-item>
      );
    });
  }

  items.push(
    <aha-menu-item
      onClick={() =>
        insertDiscoveryInsightRow(
          agent,
          undefined,
          undefined,
          toolCall.content?.preview?.title || toolCall.content?.panel?.title,
          toolCall.content?.panel?.body,
          toolCall.content?.panel?.contentType
        )
      }
    >
      Add as new insight
    </aha-menu-item>
  );

  if (items.length > 0) {
    return (
      <aha-menu-group>
        <div slot='label'>Insights</div>
        {items}
      </aha-menu-group>
    );
  } else {
    return null;
  }
}

async function embedArtifactInReference(
  toolCall: AssistantToolCall,
  reference: string
) {
  const url = `/assistant/embed/${reference}/artifact`;
  const embedResponse = await jsonFetch(url, {
    method: 'POST',
    body: JSON.stringify({
      artifact_id: toolCall.artifact?.id,
      version: toolCall.content?.data
        ? (JSON.parse(toolCall.content.data) as any).version
        : undefined,
    }),
  });

  const { success } = (await embedResponse.json()) as { success: boolean };

  if (success) {
    showNoticeMessage(`Added artifact to ${reference}`);
  } else {
    showAlertMessage(`Failed to add artifact to ${reference}`);
  }
}

function getReferenceAdds(
  toolCall: AssistantToolCall,
  agent: Agent
): React.ReactNode[] {
  const references = agent.controller.getReferencesInSentMessages();

  const ReferenceAddButton: React.FC<{
    reference: string;
    onClick: () => void;
  }> = ({ reference, onClick }) => (
    <aha-menu-item key={reference} onClick={onClick}>
      Add to {reference}
    </aha-menu-item>
  );

  // Workaround for single reference to whiteboards. Any insertions are significantly
  // cleaner to do on the frontend, so in the case that the user is inside a whiteboard,
  // we will directly drop the artifact within the frontend.
  if (references?.length === 1 && agent.contexts.assistedWhiteboard) {
    return [
      <ReferenceAddButton
        reference={references[0]}
        onClick={() => void addPrototypeToWhiteboard(toolCall, agent)}
      />,
    ];
  }

  if (references && references.length > 0) {
    return references
      .map(reference => {
        if (reference.startsWith('Report-')) {
          return;
        }

        return (
          <ReferenceAddButton
            reference={reference}
            onClick={() => void embedArtifactInReference(toolCall, reference)}
          />
        );
      })
      .filter(Boolean) as React.ReactNode[]; // Filter out undefined values
  }

  return [];
}

async function createArtifactAsNote(toolCall: AssistantToolCall, agent: Agent) {
  addPageIdToToolCall(toolCall, 'loading');
  const page = new Page({
    name:
      toolCall.content?.preview?.title ||
      toolCall.content?.panel?.title ||
      'New Note',
    project: {
      id: agent.controller.projectId,
    },
    documentType: Page.DOCUMENT_TYPE_NOTE,
  });

  if (toolCall.content?.panel?.contentType === 'text/markdown') {
    page.descriptionMarkdown = toolCall.content?.panel?.body || '';
  } else {
    // @ts-ignore
    page.description = toolCall.content?.panel?.body || '';
  }

  page.inDiscoveryParent = agent.controller.inDiscoveryParent;

  await page.save();

  await jsonFetch(`/assistant/tool_calls/${toolCall.id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      result_data: {
        page_id: page.id,
      },
    }),
  });

  addPageIdToToolCall(toolCall, page.id);
}

function addPageIdToToolCall(toolCall, pageId: RecordId | string) {
  if (toolCall.content?.data) {
    let existingData: Record<string, unknown> = {};
    if (toolCall.content?.data) {
      existingData = JSON.parse(toolCall.content.data) as Record<
        string,
        unknown
      >;
    }
    existingData.page_id = pageId;
    toolCall.content.data = JSON.stringify(existingData);
  }
}

const AS_NOTE_CONTENT_TYPES = ['text/markdown', 'text/html', 'text/plain'];
const AS_PROTOTYPE_CONTENT_TYPES = [
  'application/vnd.aha.react',
  'application/vnd.aha.react_entrypoint',
];

async function addPrototypeToWhiteboard(
  toolCall: AssistantToolCall,
  agent: Agent
) {
  const assistedWhiteboard = agent.contexts.assistedWhiteboard;
  const token = toolCall.artifact?.token;

  if (!assistedWhiteboard || !token) return;

  let version: string | undefined;
  if (toolCall.content?.data) {
    try {
      const parsedData = JSON.parse(toolCall.content.data) as {
        version?: string | number;
      };
      if (parsedData.version) {
        version = String(parsedData.version);
      }
    } catch (error) {
      console.warn(
        'Failed to parse artifact data for whiteboard version',
        error
      );
    }
  }

  try {
    await assistedWhiteboard.actionPlaceAssistantArtifact({
      token,
      version,
    });
    showNoticeMessage('Added prototype to whiteboard');
  } catch (error) {
    console.error('Error adding prototype to whiteboard:', error);
    showAlertMessage('Failed to add prototype to whiteboard');
  }
}

const artifactToolHandler: ToolHandler = {
  getToolActions(toolCall: AssistantToolCall, agent: Agent): React.ReactNode {
    if (toolCall.status !== 'complete' || !toolCall.content?.panel?.body) {
      return null;
    }

    const items: React.ReactNode[] = [];

    if (toolCall.artifact?.id) {
      items.push(...getReferenceAdds(toolCall, agent));
    }

    if (
      ['text/plan', 'text/markdown'].includes(
        toolCall.content.panel.contentType
      )
    ) {
      const discoveryAdds = getDiscoveryAdds(toolCall, agent);
      if (discoveryAdds) {
        items.push(discoveryAdds);
      }
    }

    if (AS_NOTE_CONTENT_TYPES.includes(toolCall.content.panel.contentType)) {
      const artifactData = JSON.parse(toolCall.content?.data || '{}') as Record<
        string,
        any
      >;
      if (!artifactData.page_id) {
        items.push(
          <aha-menu-group>
            <aha-menu-item
              key={'create_as_note'}
              onClick={() => void createArtifactAsNote(toolCall, agent)}
            >
              Create as note
            </aha-menu-item>
          </aha-menu-group>
        );
      }
    }

    const buttons: React.ReactNode[] = [];

    if (items.length > 0) {
      buttons.push(
        <aha-menu>
          <aha-button kind='icon' size='mini' slot='control'>
            <img src={addToSvg} className='tw-mt-1' />
          </aha-button>
          <aha-tooltip>Add to record</aha-tooltip>
          <aha-menu-content>{items}</aha-menu-content>
        </aha-menu>
      );
    }

    if (
      AS_PROTOTYPE_CONTENT_TYPES.includes(toolCall.content.panel.contentType)
    ) {
      const { inspectAppPrototypeEnabled } = agent.controller.state;

      buttons.unshift(
        <>
          <aha-button
            kind='icon'
            size='mini'
            active={inspectAppPrototypeEnabled ? true : undefined}
            onClick={() => agent.controller.actionToggleInspectAppPrototype()}
          >
            <img src={inspectElementSvg} className='tw-mt-[5px]' />
          </aha-button>
          <aha-tooltip>Select elements in the prototype</aha-tooltip>
        </>
      );
    }

    return buttons;
  },
};

Tool.register('CreateArtifactTool', artifactToolHandler);
Tool.register('EditArtifactTool', artifactToolHandler);
